import { useEffect, useState } from "react";
import { Download, X, Bell, Smartphone } from "lucide-react";

/**
 * Bottom-sheet install prompt for Android/desktop Chrome (beforeinstallprompt event).
 * Falls back to iOS instructions on Safari (no native install API).
 * Auto-hides if user already installed, dismissed within last 7 days, or app is running standalone.
 */
const DISMISS_KEY = "pfc_pwa_install_dismissed_at";
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true ||
    document.referrer.startsWith("android-app://")
  );
}

function isIos() {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function InstallPWAPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosTip, setShowIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    const tooSoon = dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400000;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!tooSoon) setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari doesn't fire beforeinstallprompt — show tip after a delay
    if (isIos() && !tooSoon) {
      const t = setTimeout(() => setShowIosTip(true), 6000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setShowIosTip(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      setShow(false);
    } else {
      dismiss();
    }
    setDeferred(null);
  };

  if (show && deferred) {
    return (
      <div className="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-auto sm:right-6 sm:max-w-sm z-[40] bg-ink-900 border border-pfc-gold/40 shadow-2xl backdrop-blur" data-testid="pwa-install-prompt">
        <div className="p-4 flex items-start gap-3">
          <img src="/icon-192.png" alt="PFC" className="w-12 h-12 shrink-0 rounded" />
          <div className="flex-1 min-w-0">
            <div className="text-pfc-gold text-[10px] tracking-[0.3em] uppercase font-sub">Install PFC App</div>
            <div className="text-white text-sm mt-0.5">Add Public Figures Campaign to your home screen — full screen, instant access, push alerts for live drops.</div>
            <div className="mt-3 flex gap-2">
              <button onClick={install} className="px-4 py-2 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.2em] text-[11px] hover:bg-pfc-gold-bright inline-flex items-center gap-1" data-testid="pwa-install-btn">
                <Download size={12} /> Install
              </button>
              <button onClick={dismiss} className="px-4 py-2 border border-white/15 text-white/60 font-sub uppercase tracking-[0.2em] text-[11px] hover:bg-white/5" data-testid="pwa-dismiss-btn">
                Not Now
              </button>
            </div>
          </div>
          <button onClick={dismiss} className="text-white/40 hover:text-white shrink-0" aria-label="Dismiss"><X size={14} /></button>
        </div>
      </div>
    );
  }

  if (showIosTip) {
    return (
      <div className="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-auto sm:right-6 sm:max-w-sm z-[40] bg-ink-900 border border-pfc-gold/40 shadow-2xl" data-testid="pwa-ios-tip">
        <div className="p-4 flex items-start gap-3">
          <Smartphone className="text-pfc-gold shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-pfc-gold text-[10px] tracking-[0.3em] uppercase font-sub">Install PFC App</div>
            <div className="text-white text-sm mt-0.5">
              Tap the <span className="text-pfc-gold">Share</span> icon in Safari, then choose <span className="text-pfc-gold">"Add to Home Screen"</span>.
            </div>
          </div>
          <button onClick={dismiss} className="text-white/40 hover:text-white shrink-0" aria-label="Dismiss"><X size={14} /></button>
        </div>
      </div>
    );
  }

  return null;
}

/** Inline push-notifications toggle the user can place inside Settings */
export function PushNotificationsToggle() {
  const [status, setStatus] = useState("default");
  const [supported, setSupported] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      setSupported(false);
      return;
    }
    setStatus(Notification.permission);
  }, []);

  if (!supported) {
    return <div className="text-white/40 text-sm">Push notifications are not supported on this device.</div>;
  }

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setStatus(perm);
      if (perm === "granted") {
        // Subscribe to push (server-side endpoint to receive subscription would be wired in a later phase)
        const reg = await navigator.serviceWorker.ready;
        try {
          await reg.showNotification("PFC Notifications enabled", {
            body: "You'll get a heads-up for new messages, live drops, and raffle wins.",
            icon: "/icon-192.png",
            badge: "/icon-192.png",
            tag: "pfc-welcome",
          });
        } catch (e) { /* show notif failed — ignore */ }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 border border-white/10 bg-ink-800/30 p-4" data-testid="push-toggle">
      <div className="flex items-center gap-3">
        <Bell size={18} className={status === "granted" ? "text-pfc-gold" : "text-white/40"} />
        <div>
          <div className="text-sm text-white">Push Notifications</div>
          <div className="text-xs text-white/50">
            {status === "granted" ? "Enabled — you'll get alerts for messages, lives, and raffles."
              : status === "denied" ? "Blocked — enable in your browser settings."
                : "Get notified for new messages, live appearances, and casino wins."}
          </div>
        </div>
      </div>
      {status !== "granted" && status !== "denied" && (
        <button onClick={enable} disabled={busy} data-testid="enable-push-btn" className="px-4 py-2 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.2em] text-[11px] hover:bg-pfc-gold-bright disabled:opacity-50">
          {busy ? "…" : "Enable"}
        </button>
      )}
      {status === "granted" && (
        <span className="text-[10px] uppercase tracking-[0.3em] font-sub text-pfc-gold" data-testid="push-on-label">On</span>
      )}
    </div>
  );
}
